import { Icons } from '@/styles/Icon';
import { sanitizeURL } from './xssProtection';
import { optimizeImageUrl, ImageOptimizationParams } from './imageOptimization';

/**
 * 백엔드에서 받은 미디어 URL을 완전한 URL로 변환
 * @param mediaPath - 백엔드에서 받은 상대 경로 또는 파일명
 * @param fallback - 기본값 (선택사항)
 * @param optimizationParams - 이미지 최적화 파라미터 (선택사항)
 * @returns 완전한 미디어 URL (sanitized and optimized)
 */
export const getMediaUrl = (
  mediaPath?: string | null,
  fallback?: string,
  optimizationParams?: ImageOptimizationParams
): string => {
  if (!mediaPath) {
    return fallback || '';
  }

  const sanitized = sanitizeURL(mediaPath);
  if (!sanitized) {
    return fallback || '';
  }

  let fullUrl: string;
  const isProduction = import.meta.env.PROD;

  // HTTP/HTTPS로 시작하는 완전한 URL인 경우
  if (sanitized.startsWith('https://') || sanitized.startsWith('http://')) {
    // 프로덕션에서만 HTTP를 HTTPS로 강제 변환
    fullUrl = isProduction ? sanitized.replace(/^http:\/\//i, 'https://') : sanitized;
  } else if (sanitized.startsWith('/assets/') || sanitized.startsWith('/src/assets/')) {
    // Vite 번들 에셋은 프론트엔드 서버에서 제공되므로 그대로 사용
    // 개발 모드: /src/assets/, 프로덕션 모드: /assets/
    fullUrl = sanitized;
  } else {
    // 백엔드 미디어 상대 경로인 경우 BASE_URL 추가
    const baseUrl = import.meta.env.VITE_BASE_URL || '';
    // 프로덕션에서만 HTTPS 강제 적용
    const finalBaseUrl = isProduction ? baseUrl.replace(/^http:\/\//i, 'https://') : baseUrl;
    fullUrl = `${finalBaseUrl}${sanitized.startsWith('/') ? '' : '/'}${sanitized}`;
  }

  // Apply image optimization if parameters are provided
  if (optimizationParams) {
    return optimizeImageUrl(fullUrl, optimizationParams);
  }

  return fullUrl;
};

/**
 * 아바타 이미지 URL 처리 (기본 아바타 포함)
 * @param avatarPath - 아바타 이미지 경로
 * @param optimizationParams - 이미지 최적화 파라미터 (선택사항)
 */
export const getAvatarUrl = (
  avatarPath?: string | null,
  optimizationParams?: ImageOptimizationParams
): string => {
  return getMediaUrl(avatarPath, Icons.defaultAvatar, optimizationParams);
};

/**
 * 커버 이미지 URL 처리 (기본 이미지 포함)
 * @param coverPath - 커버 이미지 경로
 * @param optimizationParams - 이미지 최적화 파라미터 (선택사항)
 */
export const getCoverImageUrl = (
  coverPath?: string | null,
  optimizationParams?: ImageOptimizationParams
): string => {
  return getMediaUrl(coverPath, Icons.defaultArtistBgImage, optimizationParams);
};
